import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom"; // URL params-kaga
import { toast } from "react-toastify";
import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import PageLoader from "../../helpers/PageLoader";
import NoDataAvailable from "../../helpers/NoDataAvailable";
import DuplicateClientsTable from "../../components/SalesComponent/DuplicateClientsTable";
import DeleteConfirmationOffcanvas from "../../helpers/DeleteConfirmationOffcanvas";
import SelectDropdown from "../../customStyles/SelectDropdown";

export default function DuplicateClients() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]); // User dropdown-kaga
  const [selectedCreatedBy, setSelectedCreatedBy] = useState(""); // Selected user filter

  // URL-la irunthu initial values edukkurom
  const initialPage = parseInt(searchParams.get("page")) || 1;
  const initialLimit = searchParams.get("limit") === "All" ? "All" : parseInt(searchParams.get("limit")) || 10;

  const [loading, setLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [groups, setGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  const [pagination, setPagination] = useState({
    page: initialPage,
    limit: initialLimit,
    total: 0,
    totalPages: 1,
  });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");


  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get(SummaryApi.getAllUsers.url);

      if (response.data.success) {
        const activeUsers = (response.data.data || []).filter(
          (u) => u.isActive !== false,
        );

        const formatted = activeUsers.map((u) => ({
          label: `${u.firstName} ${u.lastName} (${u.customRoleId?.roleName || u.role})`,
          value: u._id,
        }));

        setUsers(formatted);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchDuplicateClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(SummaryApi.duplicateClients.url, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          createdBy: selectedCreatedBy, // Filter value anupuroam
        }
      });

      if (response.data.success) {
        setGroups(response.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: response.data.pagination?.total || 0,
          totalPages: response.data.pagination?.totalPages || 1,
        }));
      }
    } catch (err) {
      toast.error("Failed to fetch duplicates");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, selectedCreatedBy]);


  useEffect(() => {
    fetchDuplicateClients();
  }, [fetchDuplicateClients]);

  // Frontend Search Logic: Groups-kulla irukura clients-ah search pannum
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groups;

    return groups.map(group => ({
      ...group,
      clients: group.clients.filter(client => 
        client.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.clientId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group._id.includes(searchTerm) // Phone number search
      )
    })).filter(group => group.clients.length > 0);
  }, [searchTerm, groups]);


  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
    setSearchParams({ page, limit: pagination.limit });
  };

  const handleLimitChange = (limit) => {
    setPagination(prev => ({ ...prev, page: 1, limit }));
    setSearchParams({ page: 1, limit });
  };

  const handleOpenDelete = (client) => {
    setSelectedClient(client);
    setDeleteReason("");
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteReason.trim()) {
      toast.warning("Please provide a reason for deletion");
      return;
    }

    try {
      setIsDeleting(true);
      const response = await api.patch(
        SummaryApi.deleteOneClient.url(selectedClient._id),
        { reason: deleteReason }
      );

      if (response.data.success) {
        toast.success("Client deleted successfully");
        setIsDeleteModalOpen(false);
        fetchDuplicateClients();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Deletion failed");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="bg-white rounded-xl p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Duplicate Clients</h1>
          <p className="text-sm text-gray-500 mt-1">Manage shared contact groups</p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Created By Filter Dropdown */}
                        <div className="w-64"> {/* Dropdown width-kaga oru div */}
            <SelectDropdown
              options={users}
              value={selectedCreatedBy} // 'selectedUser'-ah 'selectedCreatedBy'-nu mathiyachu
              onChange={(value) => {
                // Inga direct-ah state update matum illaama logic-aiyum serthu ezhuthuroam
                setSelectedCreatedBy(value);
                setPagination(prev => ({ ...prev, page: 1 })); // Page reset
                setSearchParams({ 
                  page: 1, 
                  limit: pagination.limit, 
                  createdBy: value 
                });
              }}
              placeholder="All Users"
              valueKey="value"
              labelKey="label"
              searchable={true}
            />
          </div>

          <input
            type="text"
            placeholder="Search in current list..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64 rounded-lg border border-gray-300 px-4 py-2 focus:border-red-500 focus:outline-none"
          />
        </div>
      </div>

      {loading ? (
        <PageLoader />
      ) : filteredGroups.length > 0 ? (
        <DuplicateClientsTable
          groups={filteredGroups}
          pagination={pagination}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onDeleteClick={handleOpenDelete}
        />
      ) : (
        <NoDataAvailable />
      )}

      <DeleteConfirmationOffcanvas
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Duplicate Client"
        isLoading={isDeleting} 
        itemName={`${selectedClient?.clientName}`}
      >
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reason for Deletion <span className="text-red-500">*</span>
          </label>
          <textarea
            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-1 focus:ring-red-500 focus:border-red-500 outline-none transition-all"
            rows="3"
            placeholder="Please explain why this client is being deleted (e.g., Duplicate entry, wrong phone number)..."
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
          />
        </div>
      </DeleteConfirmationOffcanvas>
    </div>
  );
}